import {Chip, Grow, Tooltip} from '@mui/material'
import LazyImage from 'utils/designSytem/LazyImage'
import {
  noImage,
  selectFailEFos,
  selectFailImg,
  selectSuccessEfos,
  selectSuccessImg,
} from 'assets/imageDeclear'
import dayjs from 'dayjs'
import {envImagePath} from 'GLOBAL/envVariables'
import {CustomTag} from 'pages/customer/simpleComponent/CustomTag'
import {
  globalFormatWithHyphens,
  globalSerialNumberFormatWithHyphens,
  serverList,
} from 'utils/const/globalConst'
import {formatDateOrEmpty, handleCellClick} from 'utils/func/justUtils'

/**
 * 단말정보와 이미지는 한 번의 통신에서 같이 올라온다.
 * 두 시각이 이만큼 벌어지면 한쪽만 올라온 것으로 보고 화면에서 표시한다.
 */
const DEVICE_EFOS_GAP_ALERT_MINUTES = 5

const getDeviceEfosGapMinutes = (serverConnectAt: any, imageConnectAt: any) => {
  if (!serverConnectAt || !imageConnectAt) return null
  const server = dayjs(serverConnectAt)
  const image = dayjs(imageConnectAt)
  if (!server.isValid() || !image.isValid()) return null
  return Math.abs(server.diff(image, 'minute'))
}

/**
 * 단말기에 설정된 주소를 서버 이름으로 바꾼다.
 * 단말정보는 lwm2mIp, 이미지는 imageIpPort 로 서로 다른 값이 내려온다.
 * serverList 에 없는 주소는 확인할 수 있게 원문 그대로 보여준다.
 */
const getServerLabel = (lwm2mIp: any, imageIpPort: any) => {
  if (!lwm2mIp && !imageIpPort) return ''
  const matched = serverList.find(
    (server) =>
      (lwm2mIp && server.lwm2mIp === lwm2mIp) || (imageIpPort && server.imageIpPort === imageIpPort)
  )
  return matched?.label ?? lwm2mIp ?? imageIpPort
}

const formatGapText = (minutes: number) => {
  const days = Math.floor(minutes / (60 * 24))
  const hours = Math.floor((minutes % (60 * 24)) / 60)
  const restMinutes = minutes % 60
  const parts: string[] = []
  if (days) parts.push(`${days}일`)
  if (hours) parts.push(`${hours}시간`)
  // 일·시간이 딱 떨어져도 단위 하나는 남겨서 '차이' 앞이 비지 않게 한다.
  if (restMinutes || !parts.length) parts.push(`${restMinutes}분`)
  return `${parts.join(' ')} 차이`
}

export const deviceCheckColumns = (
  setDeviceDetailData: any,
  imageDetailModalOpen: any,
  deviceDetailModalOpen: any
) => {
  return [
    {
      field: 'checkStatus',
      headerName: '승인상태',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return <>{params.row.checkStatus === false ? <span>NONE</span> : <span>PASS</span>}</>
      },
    },
    {
      field: 'deviceTypeName',
      headerName: '단말기 타입',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return <>{params.row.deviceTypeName}</>
      },
    },
    {
      field: 'serialNumber',
      // 윗줄 '단말기 S/N' 은 그룹 헤더(deviceCheckGroup)가 맡는다.
      headerName: 'EFOS S/N',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const serialNumber = globalSerialNumberFormatWithHyphens(params.row.serialNumber)
        // 검침기 미연결 단말기는 값이 비거나 문자열 'NULL' 로 내려온다.
        const meterReadSerialNumber = globalSerialNumberFormatWithHyphens(
          params.row.meterReadSerialNumber === 'NULL' ? null : params.row.meterReadSerialNumber
        )
        return (
          <div className={'row px-0 mx-0 text-center'}>
            <span>{serialNumber}</span>
            <span>{meterReadSerialNumber || '-'}</span>
          </div>
        )
      },
    },
    {
      field: 'imei',
      // 윗줄 'IMEI' 는 그룹 헤더가 맡는다.
      headerName: '온도',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const temp = params.row.temperature
        return (
          <div className={'row px-0 mx-0 text-center'}>
            <span
              className={'text-primary cursor-pointer'}
              onClick={(e) => {
                handleCellClick(e)
                setDeviceDetailData(params.row)
                deviceDetailModalOpen()
              }}
            >
              {params.row.imei}
            </span>
            <span>{temp !== null && temp <= 90 ? `${temp}°C` : '-'}</span>
          </div>
        )
      },
    },
    {
      headerName: '마지막 연결 서버',
      field: 'lwm2mIp',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const deviceServer = getServerLabel(params.row.lwm2mIp, null)
        const imageServer = getServerLabel(null, params.row.imageIpPort)
        // 단말정보와 이미지가 서로 다른 서버로 가고 있으면 한쪽만 쌓인다.
        const isMismatch = !!deviceServer && !!imageServer && deviceServer !== imageServer
        if (!isMismatch) return <>{deviceServer || imageServer || '-'}</>
        return (
          <Tooltip
            title={
              <span className={'fs-6'}>
                단말정보는 {deviceServer}, 이미지는 {imageServer} 로 설정돼 있습니다.
              </span>
            }
            TransitionComponent={Grow}
            TransitionProps={{timeout: 300}}
            placement={'top'}
            arrow={true}
          >
            <span className={'text-danger fw-bold'}>{deviceServer}</span>
          </Tooltip>
        )
      },
    },
    {
      // 윗줄 '단말정보 전송 일시' 는 그룹 헤더가 맡는다.
      headerName: '이미지 전송 일시',
      field: 'serverConnectAt',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const gap = getDeviceEfosGapMinutes(params.row.serverConnectAt, params.row.imageConnectAt)
        const isGapOver = gap !== null && gap >= DEVICE_EFOS_GAP_ALERT_MINUTES
        // 차이가 크면 오래된 쪽을 흐리게 낮춰 어느 쪽이 안 올라왔는지 보이게 한다.
        const stale = isGapOver ? 'text-gray-400' : ''
        const isImageOlder = dayjs(params.row.imageConnectAt).isBefore(params.row.serverConnectAt)
        const cell = (
          <div className={'row px-0 mx-0 text-center'}>
            <span className={isImageOlder ? '' : stale}>
              {formatDateOrEmpty(params.row.serverConnectAt, 'YYYY-MM-DD HH:mm:ss') || '-'}
            </span>
            <span className={isImageOlder ? stale : ''}>
              {formatDateOrEmpty(params.row.imageConnectAt, 'YYYY-MM-DD HH:mm:ss') || '-'}
            </span>
          </div>
        )
        if (!isGapOver) return cell
        return (
          <Tooltip
            title={
              <span className={'fs-6'}>
                단말정보와 이미지 전송 시각이 {formatGapText(gap as number)} 납니다.
              </span>
            }
            TransitionComponent={Grow}
            TransitionProps={{timeout: 300}}
            placement={'top'}
            arrow={true}
          >
            {cell}
          </Tooltip>
        )
      },
    },
    {
      field: 'image',
      headerName: '이미지',
      width: 400,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <>
            {params.row.deviceType === 'analog' ? (
              <Tooltip
                title={
                  <span className={'fs-6'}>
                    클릭하면 해당 단말기에 대한 이미지 리스트들을 볼 수 있습니다.
                  </span>
                }
                TransitionComponent={Grow}
                TransitionProps={{timeout: 300}}
                placement={'right'}
                arrow={true}
              >
                <div style={{width: '100%', height: '100%'}}>
                  <LazyImage
                    src={params.row.filePath ? `${envImagePath}${params.row.filePath}` : null}
                    alt={'device image'}
                    className={'p-2 cursor-pointer border-2'}
                    fallback={<img src={noImage} alt={'no image'} style={{width: '100%'}} />}
                    onClick={(e) => {
                      handleCellClick(e)
                      setDeviceDetailData(params.row)
                      imageDetailModalOpen()
                    }}
                  />
                </div>
              </Tooltip>
            ) : (
              <div
                className={
                  'cursor-pointer text-gray-500 w-full flex justify-center h-full items-center'
                }
              >
                NO IMAGE
              </div>
            )}
          </>
        )
      },
    },
    {
      field: 'batteryLevel',
      headerName: '배터리 잔량',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <>
            <span>{params.row.batteryLevel}%</span>
          </>
        )
      },
    },
    {
      field: 'signalStrength',
      headerName: '전파세기',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <>
            {params.row.signalStrength === null ? (
              <span>-</span>
            ) : (
              <span>{params.row.signalStrength + 'dBm'}</span>
            )}
          </>
        )
      },
    },
  ]
}

export const deviceAssignColumns = (
  setDeviceDetailData: any,
  imageDetailModalOpen: any,
  deviceDetailModalOpen: any
) => {
  return [
    {
      field: 'serialNumber',
      // 윗줄 '단말기 S/N' 은 그룹 헤더(deviceAssignGroup)가 맡는다.
      headerName: 'EFOS S/N',
      width: 220,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const serialNumber = globalSerialNumberFormatWithHyphens(params.row.serialNumber)
        // 검침기 미연결 단말기는 값이 비거나 문자열 'NULL' 로 내려온다.
        const meterReadSerialNumber = globalSerialNumberFormatWithHyphens(
          params.row.meterReadSerialNumber === 'NULL' ? null : params.row.meterReadSerialNumber
        )
        return (
          <div className={'row px-0 mx-0 text-center'}>
            <span>{serialNumber}</span>
            <span>{meterReadSerialNumber || '-'}</span>
          </div>
        )
      },
    },
    {
      field: 'imei',
      // 윗줄 'IMEI' 는 그룹 헤더가 맡는다.
      headerName: '온도',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const temp = params.row.temperature
        return (
          <div className={'row px-0 mx-0 text-center'}>
            <span
              className={'text-primary cursor-pointer'}
              onClick={(e) => {
                handleCellClick(e)
                setDeviceDetailData(params.row)
                deviceDetailModalOpen()
              }}
            >
              {params.row.imei}
            </span>
            <span>{temp !== null && temp <= 90 ? `${temp}°C` : '-'}</span>
          </div>
        )
      },
    },
    {
      headerName: '마지막 연결 서버',
      field: 'lwm2mIp',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const deviceServer = getServerLabel(params.row.lwm2mIp, null)
        const imageServer = getServerLabel(null, params.row.imageIpPort)
        const isMismatch = !!deviceServer && !!imageServer && deviceServer !== imageServer
        if (!isMismatch) return <>{deviceServer || imageServer || '-'}</>
        return (
          <Tooltip
            title={
              <span className={'fs-6'}>
                단말정보는 {deviceServer}, 이미지는 {imageServer} 로 설정돼 있습니다.
              </span>
            }
            TransitionComponent={Grow}
            TransitionProps={{timeout: 300}}
            placement={'top'}
            arrow={true}
          >
            <span className={'text-danger fw-bold'}>{deviceServer}</span>
          </Tooltip>
        )
      },
    },
    {
      field: 'deviceTypeName',
      headerName: '단말기 타입',
      width: 160,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'filePath',
      headerName: '이미지',
      width: 400,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <>
            {params.row.deviceType === 'analog' ? (
              <Tooltip
                title={
                  <span className={'fs-6'}>
                    클릭하면 해당 단말기에 대한 이미지 리스트들을 볼 수 있습니다.
                  </span>
                }
                TransitionComponent={Grow}
                TransitionProps={{timeout: 300}}
                placement={'right'}
                arrow={true}
              >
                <div style={{width: '100%', height: '100%'}}>
                  <LazyImage
                    src={params.row.filePath ? `${envImagePath}${params.row.filePath}` : null}
                    alt={'device image'}
                    className={'p-2 cursor-pointer border-2'}
                    fallback={<img src={noImage} alt={'no image'} style={{width: '100%'}} />}
                    onClick={(e) => {
                      handleCellClick(e)
                      setDeviceDetailData(params.row)
                      imageDetailModalOpen()
                    }}
                  />
                </div>
              </Tooltip>
            ) : (
              <div
                className={
                  'cursor-pointer text-gray-500 w-full flex justify-center h-full items-center'
                }
              >
                NO IMAGE
              </div>
            )}
          </>
        )
      },
    },
    {
      // 윗줄 '단말정보 전송 일시' 는 그룹 헤더가 맡는다.
      headerName: '이미지 전송 일시',
      field: 'serverConnectAt',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const gap = getDeviceEfosGapMinutes(params.row.serverConnectAt, params.row.imageConnectAt)
        const isGapOver = gap !== null && gap >= DEVICE_EFOS_GAP_ALERT_MINUTES
        // 차이가 크면 오래된 쪽을 흐리게 낮춰 어느 쪽이 안 올라왔는지 보이게 한다.
        const stale = isGapOver ? 'text-gray-400' : ''
        const isImageOlder = dayjs(params.row.imageConnectAt).isBefore(params.row.serverConnectAt)
        const cell = (
          <div className={'row px-0 mx-0 text-center'}>
            <span className={isImageOlder ? '' : stale}>
              {formatDateOrEmpty(params.row.serverConnectAt, 'YYYY-MM-DD HH:mm:ss') || '-'}
            </span>
            <span className={isImageOlder ? stale : ''}>
              {formatDateOrEmpty(params.row.imageConnectAt, 'YYYY-MM-DD HH:mm:ss') || '-'}
            </span>
          </div>
        )
        if (!isGapOver) return cell
        return (
          <Tooltip
            title={
              <span className={'fs-6'}>
                단말정보와 이미지 전송 시각이 {formatGapText(gap as number)} 납니다.
              </span>
            }
            TransitionComponent={Grow}
            TransitionProps={{timeout: 300}}
            placement={'top'}
            arrow={true}
          >
            {cell}
          </Tooltip>
        )
      },
    },
    {
      field: 'batteryLevel',
      headerName: '배터리 잔량',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <>
            <span>{params.row.batteryLevel}%</span>
          </>
        )
      },
    },
    {
      field: 'assignToId',
      headerName: '설치자 ID',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <span>
            {params.row.assignToId === null || params.row.assignToId === 'NULL'
              ? '-'
              : params.row.assignToId}
          </span>
        )
      },
    },
    // {field: 'registerId', headerName: '생성자', width: 220, align: 'center', headerAlign: 'center'},
    {
      field: 'assignToName',
      headerName: '설치자 이름',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return <span>{params.row.assignToName === null ? '-' : params.row.assignToName}</span>
      },
    },
    {
      field: 'checkName',
      headerName: '검수자',
      width: 160,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'assignStatus',
      headerName: '할당 상태',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <div>
            {params.row.assignStatus === true ? (
              <span className={'text-success fw-bolder'}>할당</span>
            ) : (
              <span className={'text-danger fw-bolder'}>미할당</span>
            )}
          </div>
        )
      },
    },
  ]
}

export const deviceInfoColumns = (
  setDeviceDetailData: any,
  deviceDetailModalOpen: any,
  imageDetailModalOpen: any
) => {
  const chipValue = (value: string) => {
    switch (value) {
      case null:
        return '기계식'
      case 'analog':
        return '기계식'
      case 'digital':
        return '디지털'
      case 'gas':
        return '가스'
      case 'PRV':
        return '압력게이지'
      default:
        return '기계식'
    }
  }
  const chipColor = (value: string) => {
    switch (value) {
      case null:
      case 'analog':
        return 'bg-rose-600 text-white'
      case 'digital':
        return 'bg-amber-500 text-white'
      case 'gas':
        return 'bg-green-700 text-white'
      case 'PRV':
        return 'bg-indigo-700 text-white'
      default:
        return 'bg-red-500 text-white'
    }
  }
  return [
    {
      field: 'serialNumber',
      headerName: '단말기 S/N',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const serialNumber = globalSerialNumberFormatWithHyphens(params.row.serialNumber)
        return <>{serialNumber}</>
      },
    },
    {
      field: 'customerNumber',
      headerName: '수용가번호',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return <span className={'text-hover-primary'}>{params.row.customerNumber}</span>
      },
    },
    {
      field: 'createAt',
      headerName: '최초 등록일자',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return <span>{dayjs(params.row.createAt).format('YYYY-MM-DD HH:mm')}</span>
      },
    },
    {
      field: 'deviceConnectAt',
      headerName: '서버연결 일시',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <div className={'row text-center'}>
            <span>{dayjs(params.row.deviceConnectAt).format('YYYY-MM-DD HH:mm')}</span>
            <span>전파세기 : {params.row.signalStrength}dBm</span>
          </div>
        )
      },
    },
    {
      field: 'deviceStatus',
      headerName: '단말기 통신상태',
      renderCell: (params: any) => {
        const cooperIdx = params?.row.cooperIdx
        return (
          <div
            className={'row w-100 h-100 text-center '}
            style={{alignContent: 'center'}}
            onClick={(e) => {
              handleCellClick(e)
              setDeviceDetailData(params.row)
              deviceDetailModalOpen()
            }}
          >
            <img
              alt={'device-image'}
              src={params.row.deviceStatus ? selectSuccessImg(cooperIdx) : selectFailImg(cooperIdx)}
              width={100}
            />
          </div>
        )
      },
      width: 160,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'efosStatus',
      headerName: '계량기 통신상태',
      renderCell: (params: any) => {
        const cooperIdx = params?.row.cooperIdx
        return (
          <>
            <div
              className={'row w-100 h-100 text-center '}
              style={{alignContent: 'center'}}
              onClick={(e) => {
                handleCellClick(e)
                setDeviceDetailData(params.row)
                deviceDetailModalOpen()
              }}
            >
              <img
                alt={'fos-image'}
                src={
                  params?.row.efosStatus ? selectSuccessEfos(cooperIdx) : selectFailEFos(cooperIdx)
                }
                width={100}
              />
            </div>
          </>
        )
      },
      width: 140,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'deviceVersion',
      headerName: '펌웨어',
      width: 170,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'batteryLevel',
      headerName: '배터리',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <span className={'fw-bold'}>
            {params.row.cooperName === '두리사업단'
              ? params.row.batteryLevel * 0.1 + 'V'
              : params.row.batteryLevel + '%'}
          </span>
        )
      },
    },
    {
      field: 'filePath',
      headerName: '이미지',
      width: 350,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return (
          <>
            {params.row.deviceType !== 'analog' ? (
              <div
                className={
                  'cursor-pointer text-gray-500 w-full flex justify-center h-full items-center'
                }
                onClick={(e) => {
                  setDeviceDetailData(params.row)
                  handleCellClick(e)
                  imageDetailModalOpen()
                }}
              >
                NO IMAGE
              </div>
            ) : (
              <div className={'row'}>
                <div
                  className={'d-flex gap-1 justify-content-end'}
                  style={{position: 'relative', top: '15px', zIndex: 888}}
                >
                  {params.row.rotate !== 0 && (
                    <Chip
                      label={`${params.row.rotate}° `}
                      color='primary'
                      sx={{fontSize: '17px'}}
                    />
                  )}
                  <CustomTag
                    label={chipValue(params.row.recognizedType)}
                    className={`text-[17px] h-[30px] ${chipColor(params.row.recognizedType)}`}
                  />
                </div>
                <img
                  alt={'device'}
                  className={'cursor-pointer'}
                  src={
                    params.row.filePath === null ? noImage : `${envImagePath}${params.row.filePath}`
                  }
                  width={320}
                  height={144}
                  style={{
                    transform: params.row.filePath
                      ? `rotate(${params?.row?.rotate}deg)`
                      : `rotate(0deg)`,
                  }}
                  onClick={(e) => {
                    setDeviceDetailData(params.row)
                    handleCellClick(e)
                    imageDetailModalOpen()
                  }}
                />
              </div>
            )}
          </>
        )
      },
    },
    {
      field: 'meterReadVersion',
      headerName: '검침기 펌웨어',
      width: 150,
      align: 'center',
      headerAlign: 'center',
    },
  ]
}

export const deviceImagesColumns = [
  {
    field: 'createAt',
    headerName: '이미지 시간',
    width: 190,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      return (
        <span className={'text-hover-danger cursor-pointer text-primary fw-bold'}>
          {dayjs(params.row.createAt)?.format('YYYY-MM-DD HH:mm:ss')}
        </span>
      )
    },
  },
  {
    field: 'customerNumber',
    headerName: '수용가번호',
    width: 180,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      return <>{globalFormatWithHyphens(params?.row.officeId, params?.row.customerNumber)}</>
    },
  },
  {
    field: 'serialNumber',
    headerName: '단말기 S/N',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      const serialNumber = globalSerialNumberFormatWithHyphens(params.row.serialNumber)
      return <span>{serialNumber}</span>
    },
  },
  {
    field: 'filePath',
    headerName: '이미지',
    width: 300,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      return (
        <img
          className={'cursor-pointer'}
          alt={'이미지'}
          src={envImagePath + params.row.filePath}
          width={'100%'}
        />
      )
    },
  },
  {
    field: 'meterDiameter',
    headerName: '구경',
    width: 100,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      return <span>{params.row.meterDiameter}mm</span>
    },
  },
  {
    field: 'checkValue',
    headerName: '최초 인식값',
    width: 130,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      return (
        <>
          {params.row.checkDecimalValue === null || params.row.checkDecimalValue === '' ? (
            <span>{params.row.checkValue}</span>
          ) : (
            <span>{params.row.checkValue + '.' + params.row.checkDecimalValue?.split('.')[1]}</span>
          )}
        </>
      )
    },
  },
  {
    field: 'updateCheckValue',
    headerName: '수정 인식값',
    width: 130,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      return (
        <>
          {params.row.updateCheckValue === null ? (
            <span>-</span>
          ) : (
            <span>{params.row.checkValue}</span>
          )}
        </>
      )
    },
  },
  {
    field: 'updateId',
    headerName: '수정자 ID',
    width: 120,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => {
      return <span>{params.row.updateId === null ? '-' : params.row.updateId}</span>
    },
  },
  {
    field: 'processStatus',
    headerName: '이미지 처리상태',
    width: 150,
    align: 'center',
    headerAlign: 'center',
  },
]
